import {MePermissionsDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace} from './helper';
import {NavigablePage, PAGE_PERMISSION} from 'src/permissions/pages';

/**
 * Where to send someone who has just logged in, or who asked for a page they may not see.
 *
 * One function, because there were three: `routes.tsx` picked an index route, `App.tsx` branched on
 * the role, and `LoginForm` branched on `userType`. They disagreed, and each would have needed its own
 * fix once role names stopped being a fixed list.
 *
 * The rule is "the first page you can actually open", in a fixed order of usefulness. That answer
 * stays correct for a role nobody has invented yet, which a switch cannot.
 */
/**
 * Pages worth landing on, in order of usefulness. The permission each needs comes from the same map
 * the router and the menu read, so this cannot drift into offering a page the router then refuses —
 * which is what a third hand-written copy of the same list would eventually do.
 */
const CANDIDATES: NavigablePage[] = [
    AppRouteEnum.locations,
    AppRouteEnum.chargers,
    AppRouteEnum.chargingSessions,
    AppRouteEnum.users,
    AppRouteEnum.invoices,
    AppRouteEnum.contracts,
];

/** Always reachable: it needs no permission, so it is a landing place that cannot fail. */
export const FALLBACK_ROUTE = AppRouteSpace([AppRouteEnum.app, AppRouteEnum.welcome]);

export function resolveLandingRoute(access: MePermissionsDTO | null): string {
    if (!access) {
        return FALLBACK_ROUTE;
    }
    const permissions = new Set(access.permissions);
    // Any one of them: a page can be reachable by more than one permission, and landing somebody on
    // the first page they can open must ask the same question the page and the menu ask.
    const page = CANDIDATES.find((candidate) => {
        const required = PAGE_PERMISSION[candidate];
        const names = Array.isArray(required) ? required : [required as string];
        return names.some((name) => permissions.has(name));
    });
    return page ? AppRouteSpace([AppRouteEnum.app, page]) : FALLBACK_ROUTE;
}
